import { useState } from "react";
import { Star, MessageCircle, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/shared/Reveal";
import { ConstellationField, OrbitArcs, CelestialRing } from "@/components/shared/Motifs";
import { CtaButton } from "@/components/shared/CtaButton";
import { LanguageToggle } from "@/components/shared/LanguageToggle";
import { PhoneFrame, ReportContent } from "@/components/report/DailyReport";
import { useLanguage } from "@/context/LanguageContext";
import { CONTENT } from "@/i18n/content";
import { REPORTS } from "@/data/reports";

export function Hero() {
  const { lang } = useLanguage();
  const t = CONTENT[lang].hero;
  const [heroLang, setHeroLang] = useState(lang);
  const report = REPORTS[heroLang];

  return (
    <section className="relative overflow-hidden bg-cream pt-28 pb-16 sm:pt-32 lg:pt-36 lg:pb-24" data-testid="hero">
      {/* Motifs */}
      <ConstellationField className="pointer-events-none absolute -right-20 -top-10 h-[520px] w-[520px] text-navy/[0.06]" />
      <OrbitArcs className="pointer-events-none absolute -left-32 top-40 h-[420px] w-[420px] text-gold/[0.08] animate-float" />
      <div className="pointer-events-none absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-gold/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:gap-8">
        {/* Left */}
        <div className="text-center lg:text-left">
          <Reveal delay={80}>
            <h1 className="mt-6 font-heading text-4xl font-semibold leading-[1.08] text-navy sm:text-5xl lg:text-6xl text-balance">
              {t.titleA}{" "}
              <span className="relative inline-block text-gold">
                {t.titleHighlight}
                <span className="absolute -bottom-1 left-0 h-[3px] w-full rounded-full bg-gold/30" />
              </span>
            </h1>
          </Reveal>

          <Reveal delay={160}>
            <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-navy/70 lg:mx-0">
              {t.sub}
            </p>
          </Reveal>

          <Reveal delay={240}>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row lg:justify-start">
              <CtaButton variant="primary" testId="hero-cta-primary" className="w-full sm:w-auto">
                <MessageCircle className="h-4 w-4" />
                {t.ctaPrimary}
              </CtaButton>
              <CtaButton variant="outline" scrollTo="sample" testId="hero-cta-secondary" className="w-full sm:w-auto">
                {t.ctaSecondary}
                <ArrowRight className="h-4 w-4" />
              </CtaButton>
            </div>
          </Reveal>

          <Reveal delay={320}>
            <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row sm:items-center lg:justify-start">
              <div className="flex items-center gap-1.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                ))}
                <span className="ml-1 text-sm font-semibold text-navy">4.8/5</span>
              </div>
              <span className="hidden h-4 w-px bg-navy/15 sm:block" />
              <p className="text-sm text-navy/60">{t.trust}</p>
            </div>
            <p className="mt-3 text-xs font-medium uppercase tracking-[0.14em] text-navy/45">
              {t.langLine}
            </p>
          </Reveal>
        </div>

        {/* Right — phone */}
        <Reveal delay={200} className="flex flex-col items-center gap-5 lg:items-end">
          <LanguageToggle
            value={heroLang}
            onChange={setHeroLang}
            testIdPrefix="hero-lang"
            className="border-navy/15 bg-white/80"
          />
          <div className="relative">
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[540px] w-[540px] -translate-x-1/2 -translate-y-1/2">
              <CelestialRing className="h-full w-full text-gold/25 animate-spin-slow" />
            </div>
            <div className="pointer-events-none absolute -inset-6 rounded-[3rem] bg-gradient-to-b from-white/60 to-transparent blur-xl" />
            <PhoneFrame className="relative">
              <ReportContent report={report} />
            </PhoneFrame>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
